import React, { Fragment } from 'react';
import { Field } from 'formik';
import FormikInput from 'pages/components/Formik/Input';
import get from 'lodash/get';
import Bone from '@lightspeed/cirrus-bone';
import { Divider } from '@lightspeed/flame/Divider';
import Input from '@lightspeed/cirrus-input';
import Text from '@lightspeed/cirrus-text';
import { formatNumberInput } from 'pages/utils/input';
import Format from 'helpers/format';

import { formatMoney, validateMoney, keyExists } from './utils';

export const updateTotal = ({ subtotal, shipCost, otherCost, taxAmount } = {}) =>
  Format.toMoney(
    parseFloat(validateMoney(taxAmount || 0, true)) +
      parseFloat(validateMoney(subtotal || 0, true)) +
      parseFloat(validateMoney(shipCost || 0, true)) +
      parseFloat(otherCost || 0)
  );

const VendorReturnFormCost = ({
  enabledFields,
  vendorReturn = {},
  isClosed,
  values = {},
  setFieldValue,
}) => {
  const hasVendorReturnKey = keyExists(vendorReturn || {});
  const currencySymbol = get(window, 'merchantos.locale.currency_symbol', '');
  const subtotal = get(vendorReturn, 'subtotal.value', 0);
  const shipCost = get(values, 'shipCost', 0);
  const otherCost = get(values, 'otherCost', 0);
  const taxAmount = get(vendorReturn, 'taxAmount.value', '0');

  return (
    <Fragment>
      <Text heading4 className="cr-text-center" style={{ marginBottom: '12px' }}>
        Cost summary
      </Text>
      {hasVendorReturnKey('subtotal') && (
        <div className="cr-flex cr-items-center cr-justify-between cr-mb-2">
          <Text>Return value</Text>
          <Text body>
            <var>
              {currencySymbol}
              {formatMoney(subtotal)}
            </var>
          </Text>
        </div>
      )}
      {hasVendorReturnKey('shipCost') && (
        <div className="cr-flex cr-items-center cr-justify-between cr-mb-2">
          <Text>Shipping</Text>
          <div className="cr-ml-2">
            {isClosed ? (
              <Text body>
                <var>
                  {currencySymbol}
                  {shipCost}
                </var>
              </Text>
            ) : (
              <Field
                name="shipCost"
                render={fieldProps => (
                  <FormikInput
                    {...fieldProps}
                    type="text"
                    prefix={currencySymbol}
                    disabled={!~enabledFields.indexOf('shipCost')}
                    onChange={({ target: { value } = {} } = {}) =>
                      setFieldValue('shipCost', formatNumberInput(value))
                    }
                    onFocus={event => {
                      window.selectField(event.target);
                    }}
                    onBlur={event => {
                      const formattedShipCost = validateMoney(event.target.value, true);

                      setFieldValue('shipCost', formattedShipCost);
                    }}
                    style={{ textAlign: 'right' }}
                    data-test="vendor-return-ship-cost-input"
                  >
                    {inputProps => <Input {...inputProps} />}
                  </FormikInput>
                )}
              />
            )}
          </div>
        </div>
      )}
      {hasVendorReturnKey('otherCost') && (
        <div className="cr-flex cr-items-center cr-justify-between cr-mb-2">
          <Text>Other</Text>
          <div className="cr-ml-2">
            {isClosed ? (
              <Text body>
                <var>
                  {currencySymbol}
                  {otherCost}
                </var>
              </Text>
            ) : (
              <Field
                name="otherCost"
                render={fieldProps => (
                  <FormikInput
                    {...fieldProps}
                    type="text"
                    prefix={currencySymbol}
                    disabled={!~enabledFields.indexOf('otherCost')}
                    onChange={({ target: { value } = {} } = {}) =>
                      setFieldValue('otherCost', formatNumberInput(value))
                    }
                    onFocus={event => {
                      window.selectField(event.target);
                    }}
                    onBlur={event => {
                      const formattedOtherCost = validateMoney(event.target.value);

                      setFieldValue('otherCost', formattedOtherCost);
                    }}
                    style={{ textAlign: 'right' }}
                    data-test="vendor-return-other-cost-input"
                  >
                    {inputProps => <Input {...inputProps} />}
                  </FormikInput>
                )}
              />
            )}
          </div>
        </div>
      )}

      {hasVendorReturnKey('subtotal') &&
        hasVendorReturnKey('shipCost') &&
        hasVendorReturnKey('otherCost') && (
          <React.Fragment>
            <Divider className="cr-mb-2" />
            <div className="cr-flex cr-items-center cr-justify-between">
              <Text bodyLarge bold>
                Total
              </Text>
              <Text bodyLarge bold>
                <var data-test="vendor-return-total-cost-text">
                  {updateTotal({ subtotal, shipCost, otherCost, taxAmount })}
                </var>
              </Text>
            </div>
          </React.Fragment>
        )}
    </Fragment>
  );
};

export const VendorReturnFormCostSkeleton = () => (
  <Fragment>
    <div className="cr-flex cr-justify-center cr-mb-4">
      <Bone width="40%" height="1.5rem" />
    </div>
    <div className="cr-width-full cr-flex cr-justify-between cr-mb-2">
      <Bone width="30%" />
      <Bone width="40%" />
    </div>
    <div className="cr-width-full cr-flex cr-justify-between cr-mb-2">
      <Bone width="30%" />
      <Bone width="40%" height="1.875rem" />
    </div>
    <div className="cr-width-full cr-flex cr-justify-between cr-mb-2">
      <Bone width="30%" />
      <Bone width="40%" height="1.875rem" />
    </div>
    <Divider className="cr-mb-2" />
    <div className="cr-width-full cr-flex cr-justify-between">
      <Bone width="30%" height="1.875rem" />
      <Bone width="40%" height="1.875rem" />
    </div>
  </Fragment>
);

export default VendorReturnFormCost;
